import React, {useContext} from "react";
import {useAsync2} from "nate-react-api-helpers";
import {useNavigate} from "react-router-dom";
import {SelectEditor} from "../../../../misc/scroller/SelectEditor";
import {EditContext} from "../../../../misc/scroller/EditProvider";
import {api} from "../../../../api/API";
import {useProjectId} from "../../shopdrawing/ProjectName";
import {useSnackAction} from "../../shopdrawing/hardware/SnackAction";
import {DropdownDataLoading} from "../../settings/DropDowns";
import {doorElevationPickerOptions, legacyPickerName, withLegacyDoorElevation} from "../../shopdrawing/openings/DoorElevationHelpers";

// quote-mode door elevation picker — the quote-opening equivalent of the shop-drawing
// PickDoorElevation. Lists the alternative's existing door elevations (plus the global ones and
// the file-based v1 names); selecting one links
// this quote opening (no identity change, unlike the screen picker). The free-solo "draw
// new" option unlinks the opening and opens it in the elevation tool.
export function PickDoorElevation(props: {
    anchor: any;
    row: {id: number};
    width: number;
    alternative: number;
    onCancel(): void;
}) {
    const project = useProjectId();
    const editCtx = useContext(EditContext);
    const navigate = useNavigate();

    // scope the list to this quote opening + alternative so the server hides doors of an
    // incompatible opening type.
    const list = useAsync2((p: number) => api.elevationTool.listDoorElevations({project: p, quoteOpening: props.row.id, alternative: props.alternative}), project, [project, props.alternative]);

    const adopt = useSnackAction(async (door: number) => {
        await api.elevationTool.adoptDoor({project, quoteOpening: props.row.id, door});
        await editCtx.reload(true, [props.row.id]);
        props.onCancel();
    }, {success: "Door elevation linked"});

    // free-solo: unlink this quote opening's door elevation, then open the tool, passing the
    // typed name so it auto-saves a fresh door elevation under it.
    const drawNew = useSnackAction(async (name: string) => {
        await api.elevationTool.unlinkDoor({project, quoteOpening: props.row.id});
        navigate(`/project/${project}/quote/${props.alternative}/elevation-tool?quoteOpening=${props.row.id}&alternative=${props.alternative}&workspace=door&autoSaveName=${encodeURIComponent(name.trim())}`);
    }, {success: "Unlinked — opening elevation tool"});

    // a v1 name unlinks the drw door and sets the legacy columns in the same request.
    const pickLegacy = useSnackAction(async (name: string) => {
        const list = await api.quoteOpenings.list({project, alternative: props.alternative, ids: props.row.id.toString()});
        await api.elevationTool.unlinkDoor({project, quoteOpening: props.row.id, legacyQuoteOpening: withLegacyDoorElevation(list.data[0], name)});
        await editCtx.reload(true, [props.row.id]);
        props.onCancel();
    }, {success: "Door elevation set"});

    if(list.loading) return <DropdownDataLoading anchor={props.anchor} width={props.width} />;

    const options = doorElevationPickerOptions(list.asList);

    const select = (value: string, freeSolo: boolean) => {
        if(freeSolo) return drawNew.callback(value);

        const legacy = legacyPickerName(value);
        if(legacy !== null) return pickLegacy.callback(legacy);

        return adopt.callback(parseInt(value));
    };

    return (
        <SelectEditor
            anchor={props.anchor}
            options={options}
            value=""
            initialValue=""
            freeSolo
            onBlur={props.onCancel}
            onSelect={select}
        />
    );
}
